/**
 * RecordDecisionPage — Contractor records a client's acceptance decision
 * received via phone, email, or hard copy (non-e-sign channels).
 *
 * PT-38 (2026-07-28). Route: /quotes/:id/record-decision
 *
 * Uses the same B1 `acknowledgeSignature` endpoint as the e-sign path (PT-37),
 * but with `channel` set to the selected non-e-sign channel and `recorded_by`
 * set to the current user. No signature pad — the contractor is attesting that
 * the client accepted via another medium.
 *
 * Consumes DocumentReviewSurface (PT-36) for selection resolution.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';
import { Spinner } from '../ui/Spinner';
import DetailPageHeader from '../ui/DetailPageHeader';
import DocumentReviewSurface from '../shared/DocumentReviewSurface';
import computeSigningTotals from '../signature/computeSigningTotals';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../ui/Toast';
import api from '../../services/api';
import { readEngineLine, resolveMarginContext } from '../../quote-builder/readEngineLine';
import { isOptionLine } from '../../quote-builder/separateOptions';
import { projectAlternatesFlat, deriveInitialSelections } from '../../quote-builder/selectionAdapter';
import { acceptedQuoteView } from '../../quote-builder/acceptedView';
import { reopenConfirmOptions } from '../../quote-builder/quoteLifecycle';
import { quotesAPI } from '../../quote-builder/quoteAPI';
import { useConfirm } from '../ui/ConfirmationModal';

// useConfirm throws outside a <ConfirmProvider> (App mounts one). Without a
// provider Reopen is simply never confirmed.
function useConfirmSafe() {
  try {
    return useConfirm();
  } catch (e) {
    return async () => false;
  }
}


export default function RecordDecisionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirmSafe();

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customerName, setCustomerName] = useState('');

  // Selection state
  const [selections, setSelections] = useState({ alternates: [], options: [] });
  const [expandedLineItems, setExpandedLineItems] = useState([]);
  const [computedTotal, setComputedTotal] = useState(0);

  // Decision state
  const [saving, setSaving] = useState(false);

  // Load quote
  //
  // D-041 (PT-53 §53.1a): read the authoritative total the landing table reads
  // (`total_amount` / `totalAmount`, stamped from `baseSummary.bidTotal` per PT-52
  // §4). Fall back to summing per-line `line_total`/`lineTotal` ONLY if the
  // authoritative field is absent (unsaved-quote path; should not happen in
  // production). Per D-04, the server derives the authoritative total.
  //
  // D-042 (PT-53 §53.1b): prefer `customer_name` (stamped by
  // `assembleQuotePayload`); fall back to a `customer_id` lookup. Never fall
  // back to 'Unknown' silently — that is a data-integrity signal.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await api.getQuote(id);
        // IDI-416: an already-accepted quote shows the lines that were
        // accepted (its snapshot), not what the builder holds now.
        const data = acceptedQuoteView(res?.data || res);
        if (!cancelled && data) {
          setQuote(data);
          const lineItems = data.line_items || data.lineItems || [];
          // Seed selections via the adapter that projects nested alternates
          // into the flat sibling model QuoteSelectionPanel expects.
          const expanded = projectAlternatesFlat(lineItems);
          setExpandedLineItems(expanded);
          setSelections(deriveInitialSelections(expanded));
          // D-041 (2026-07-30) / PT-53A W2.1 DEF-001 (2026-07-29): initial
          // total resolution.
          //   1. `total_amount` / `totalAmount` — authoritative when present.
          //   2. Sum of engine-derived per-line totals (readEngineLine) —
          //      protects against DEF-001 shape: a quote whose stored
          //      totals are stale or zero but whose line items have
          //      cost + margin from which totals can be derived.
          //   3. Sum of wire `line_total` — last-resort fallback.
          // NEVER default to 0. `$0.00` on Record Decision is DEF-001;
          // if we can't resolve a total at all, the caller sees the
          // acknowledgement CTA gated by a warning — not a silent zero.
          //
          // §1a fix (2026-08-07): compute ADDITIVELY from accepted lines.
          //   totalAmount is unreliable as a base to subtract from — for some
          //   records it INCLUDES options, for others it EXCLUDES them (Q-0029:
          //   totalAmount 800065, subtotal 761099, option 750000). Subtracting
          //   option totals from a base whose composition is unknown produces
          //   wrong numbers on screen next to a correct line list (BP-6).
          //
          //   Correct model: sum the lines the user has accepted.
          //     accepted = base lines + checked options + chosen alternates
          //   Options default to unchecked (A2), so initial total = base lines only.
          const marginCtx = resolveMarginContext(data, null);
          const lineTotalOf = (li) => {
            const eng = readEngineLine(li, marginCtx).total;
            if (eng > 0) return eng;
            return Number(li.line_total ?? li.lineTotal ?? li.total ?? 0) || 0;
          };
          // Base lines only (options unchecked by default, alternates all present in initial state)
          const baseTotal = (lineItems || [])
            .filter((li) => !isOptionLine(li))
            .reduce((s, li) => s + lineTotalOf(li), 0);
          setComputedTotal(baseTotal);
          // D-042: customer name resolution
          const stampedName = data.customer_name || data.customerName;
          if (stampedName) {
            setCustomerName(stampedName);
          } else {
            const customerId = data.customer_id || data.customerId;
            if (customerId) {
              try {
                const cres = await api.getCustomer(customerId);
                const cdata = cres?.data || cres;
                if (!cancelled && cdata) {
                  setCustomerName(cdata.name || cdata.customer_name || cdata.customerName || '');
                }
              } catch (_) {
                // Leave customerName empty — render layer surfaces the gap.
              }
            }
          }
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load quote.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  // Selection change → recompute total
  //
  // D-041 (PT-53 §53.1a): base is the authoritative `total_amount`. Subtract
  // the `line_total` of any declined options. Only fall back to summing line
  // items when the authoritative field is absent.
  const handleSelectionChange = useCallback((newSelections) => {
    setSelections(newSelections);
    // §1a fix: compute additively from ACCEPTED lines. Never subtract from a
    // stored total whose composition is unknown (BP-6).
    // Use expandedLineItems (includes projected alternates as siblings)
    const acceptedOptionIds = new Set(
      (newSelections.options || []).filter((o) => o.accepted).map((o) => o.line_id)
    );
    const chosenAlternateIds = new Set(
      (newSelections.alternates || []).map((a) => a.chosen_line_id)
    );
    const marginCtx = resolveMarginContext(quote, null);
    const lineTotalOf = (li) => {
      const eng = readEngineLine(li, marginCtx).total;
      if (eng > 0) return eng;
      return Number(li.line_total ?? li.lineTotal ?? li.total ?? 0) || 0;
    };
    const isAcceptedLine = (li) => {
      const id = li.id || li.line_id;
      // Options: only if user checked them
      if (isOptionLine(li)) return acceptedOptionIds.has(id);
      // Alternates: only the chosen one from each group
      const groupId = li.alternate_group_id || li.alternateGroupId;
      if (groupId) return chosenAlternateIds.has(id);
      // Base lines: always accepted
      return true;
    };
    const total = expandedLineItems
      .filter(isAcceptedLine)
      .reduce((s, li) => s + lineTotalOf(li), 0);
    setComputedTotal(total);
  }, [quote, expandedLineItems]);

  // Approve & Convert — ONE conversion surface (operator ruling 2026-08-09 §2).
  //
  // Two-step commit:
  //   1. recordDecision(id, {channel, recorder})
  //      → sets status='won', wonAt, signatureStatus='acknowledged'
  //      → backend materialises the acceptedConfiguration snapshot on next read
  //   2. convertQuoteToJob(id, {alternates, options})
  //      → creates the job carrying the user's selected lines
  //
  // Both are business-authoritative for THIS user's session: the user has
  // reviewed alternates/options and clicked Approve & Convert.
  //
  const handleRecordDecision = async () => {
    try {
      setSaving(true);
      setError(null);

      // Single-step conversion (operator ruling 2026-08-09):
      // A successful conversion IS the win. The backend's convert_to_job
      // handler sets status='won', wonAt, signatureStatus='acknowledged'
      // on the quote in the same transaction that creates the job.
      // There is no separate "record the win" step.
      //
      // IDI-413: for e-signed (acknowledged) quotes, omit selections so
      // the backend uses the AcceptedConfiguration snapshot — the frozen
      // record of what the customer actually chose. Sending frontend-
      // derived defaults would overwrite the customer's signed choices.
      const isAcknowledged = (quote?.signatureStatus || quote?.signature_status) === 'acknowledged';
      // IDI-416: for a quote that was never signed, these choices become the
      // AcceptedConfiguration snapshot server-side (the same record e-signing
      // writes); the job and every screen then read it. An acknowledged quote
      // with no snapshot is refused (409) with a message to re-send.
      const result = await api.convertQuoteToJob(id, isAcknowledged
        ? null  // no selections → backend uses AcceptedConfiguration
        : {
            alternates: selections.alternates,
            options: selections.options,
            recorder: user?.name || user?.email || 'Unknown user',
          }
      );

      toast.success('Quote approved and converted to job.');
      const jobId = result?.job?.id || result?.data?.job?.id || result?.job_id;
      if (jobId) {
        navigate(`/jobs/${jobId}`);
      } else {
        navigate(`/quotes/${id}`);
      }
    } catch (err) {
      const body = err?.response?.data || err?.data;
      const message = body?.message || body?.error || err?.message;
      // IDI-416: the server's own wording for a quote with no accepted record.
      setError(body?.error === 'no_accepted_configuration' ? message : `Failed to convert: ${message || 'please try again.'}`);
    } finally {
      setSaving(false);
    }
  };

  // IDI-814 (D1): the total convert charges — chosen lines − discounts + tax
  // at the quote's stored rate — via the signing page's rule (IDI-370).
  // `computedTotal` stays the selections subtotal. Above `if (loading)`.
  const totals = useMemo(() => computeSigningTotals(computedTotal, quote), [computedTotal, quote]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner />
      </div>
    );
  }

  const isAlreadyAccepted = (quote?.signatureStatus || quote?.signature_status) === 'acknowledged';
  // P0 (2026-08-09): "no further action" is only true when the quote is ALSO
  // already converted. An acknowledged quote with no job still needs conversion.
  const isFullyConverted = isAlreadyAccepted && !!(quote?.convertedJobId || quote?.converted_job_id);
  // IDI-568 (D4, D-08): e-signed choices are locked here; changing them goes
  // back to the builder through the same Reopen and Edit as the builder's.
  const canReopen = isAlreadyAccepted && !(quote?.convertedJobId || quote?.converted_job_id);
  const handleReopen = async () => {
    const ok = await confirm(reopenConfirmOptions(true));
    if (!ok) return;
    try {
      await quotesAPI.reopen(id);
      toast.success('Quote reopened for editing');
      navigate(`/quotes/${id}/edit`);
    } catch (err) {
      toast.error('Failed to reopen: ' + (err?.message || 'Unknown error'));
    }
  };

  return (
    <div className="space-y-6">
      <DetailPageHeader
        title="Record Client Decision"
        subtitle={`Quote ${quote?.quote_number || quote?.quoteNumber || id}`}
        backTo={`/quotes/${id}`}
        backLabel="Back to quote"
        actions={canReopen ? (
          <Button variant="secondary" onClick={handleReopen} data-testid="idi568-reopen">Reopen and Edit</Button>
        ) : undefined}
      />

      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isFullyConverted && (
        <Alert variant="info">
          This quote has been accepted and converted to a job. No further action needed.
        </Alert>
      )}

      {/* Document preview with selections */}
      <DocumentReviewSurface
        documentType="quote"
        document={quote}
        selections={selections}
        onSelectionChange={handleSelectionChange}
        readOnly={isAlreadyAccepted}
        lineItems={expandedLineItems}
        computedTotal={totals.total}
        totalsBreakdown={totals}
        totalCents={isAlreadyAccepted ? (quote?.lockedTotalCents ?? quote?.locked_total_cents) : undefined}
        customerView={false} /* internal authenticated preview — DEF-088 W1.1c */
        showBaseLines /* A2: render base lines as locked in scope block */
      >
        <div className="px-6">
          <h2 className="text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {quote?.title || quote?.quote_number || 'Quote'}
          </h2>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
            {/* D-042: prefer stamped `customer_name`; lookup fallback populates
                `customerName` state. Empty string surfaces the data-integrity
                gap explicitly rather than masking it as 'Unknown'. */}
            Customer: {quote?.customer_name || quote?.customerName || customerName || '—'}
          </p>
        </div>
      </DocumentReviewSurface>

      {/* Approve & Convert (operator ruling 2026-08-09).
          Channel is OPTIONAL. The primary action is the conversion.
          Hidden when already fully converted (banner + CTA would contradict). */}
      {!isFullyConverted && (
      <Card className="p-4 space-y-4">
        <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          Recording as: {user?.name || user?.email || 'Current user'}
        </p>
        <div className="flex justify-end">
          <Button
            onClick={handleRecordDecision}
            disabled={saving}
          >
            {saving ? "Converting…" : "Approve & Convert"}
          </Button>
        </div>
      </Card>
      )}
    </div>
  );
}
